<template>
    <div class="nc-qro">

        <!-- Левая колонка: категории -->
        <div class="nc-qro__sidebar">
            <button
                v-for="cat in categories"
                :key="cat.id"
                type="button"
                class="nc-qro__cat"
                :class="{ 'is-active': activeCategory && activeCategory.id === cat.id }"
                @click="activeCategory = cat"
            >
                {{ cat.name }}
            </button>
        </div>

        <!-- Правая колонка: поиск + фразы -->
        <div class="nc-qro__content">

            <!-- Пошук -->
            <div class="nc-qro__search">
                <svg class="nc-qro__search-icon" width="14" height="14" aria-hidden="true">
                    <use href="#icon-search"></use>
                </svg>
                <input
                    ref="searchInput"
                    v-model="searchQuery"
                    type="text"
                    class="nc-qro__search-input"
                    placeholder="Поиск..."
                    autocomplete="off"
                />
                <button
                    v-if="searchQuery"
                    type="button"
                    class="nc-qro__search-clear"
                    @click="clearSearch"
                >
                    <svg width="10" height="10" aria-hidden="true"><use href="#icon-close"></use></svg>
                </button>
            </div>

            <!-- Список карток фраз -->
            <div class="nc-qro__list">

                <!-- Результати пошуку -->
                <template v-if="searchQuery">
                    <button
                        v-for="phrase in filteredPhrases"
                        :key="phrase.id"
                        type="button"
                        class="nc-qro__card"
                        @mouseenter="showTooltip($event, phrase)"
                        @mouseleave="hideTooltip"
                        @click="$emit('select', phrase)"
                    >
                        <p class="nc-qro__card-text">{{ phrase.name }}</p>
                    </button>
                    <div v-if="!filteredPhrases.length" class="nc-qro__empty">
                        Ничего не найдено
                    </div>
                </template>

                <!-- Фрази вибраної категорії -->
                <template v-else-if="activeCategory">
                    <button
                        v-for="phrase in activeCategory.phrases"
                        :key="phrase.id"
                        type="button"
                        class="nc-qro__card"
                        @mouseenter="showTooltip($event, phrase)"
                        @mouseleave="hideTooltip"
                        @click="$emit('select', phrase)"
                    >
                        <p class="nc-qro__card-text">{{ phrase.name }}</p>
                    </button>
                    <div v-if="!activeCategory.phrases.length" class="nc-qro__empty">
                        Нет фраз
                    </div>
                </template>

                <div v-else class="nc-qro__empty">
                    Выберите категорию
                </div>

            </div>
        </div>

        <!-- Тултип с полным текстом (position: fixed, не режется overflow) -->
        <div
            v-if="tooltip.visible"
            ref="tooltip"
            class="nc-qro__tooltip"
            :style="tooltip.style"
        >
            {{ tooltip.text }}
        </div>

    </div>
</template>

<script>
import { mapGetters } from 'vuex'

const TOOLTIP_W  = 220
const TOOLTIP_MARGIN = 8

export default {
    name: 'NewChatQuickRepliesOverlay',

    data() {
        return {
            searchQuery:    '',
            activeCategory: null,
            tooltip: { visible: false, text: '', style: {} },
        }
    },

    computed: {
        ...mapGetters('newChat', ['reference']),

        categories() {
            const list = this.reference && this.reference.quick_phrases_list
            if (!list) return []
            return list.map(cat => ({
                id:      cat.id,
                name:    cat.name || cat.title || '',
                phrases: cat.phrases || [],
            }))
        },

        filteredPhrases() {
            const q = this.searchQuery.trim().toLowerCase()
            if (!q) return []
            const result = []
            for (const cat of this.categories) {
                for (const phrase of cat.phrases) {
                    if (phrase.name.toLowerCase().includes(q)) {
                        result.push(phrase)
                    }
                }
            }
            return result
        },
    },

    watch: {
        categories(cats) {
            if (cats.length && !this.activeCategory) {
                this.activeCategory = cats[0]
            }
        },
    },

    mounted() {
        if (this.categories.length) {
            this.activeCategory = this.categories[0]
        }
        this.$nextTick(() => {
            if (this.$refs.searchInput) this.$refs.searchInput.focus()
        })
    },

    methods: {
        clearSearch() {
            this.searchQuery = ''
            this.$refs.searchInput && this.$refs.searchInput.focus()
        },

        showTooltip(event, phrase) {
            const rect = event.currentTarget.getBoundingClientRect()
            const vpW  = window.innerWidth
            const vpH  = window.innerHeight

            // Горизонтально: справа, если не влезает — слева
            let left = rect.right + TOOLTIP_MARGIN
            if (left + TOOLTIP_W > vpW) {
                left = rect.left - TOOLTIP_W - TOOLTIP_MARGIN
            }

            // Показываем сначала у верха карточки — после рендера скорректируем
            this.tooltip = {
                visible: true,
                text:    phrase.name,
                style: {
                    top:       rect.top + 'px',
                    left:      left + 'px',
                    width:     TOOLTIP_W + 'px',
                    maxHeight: (vpH - TOOLTIP_MARGIN * 2) + 'px',
                    opacity:   '0',   // скрыт пока не скорректировали позицию
                },
            }

            // После рендера читаем реальную высоту и поправляем top
            this.$nextTick(() => {
                const el = this.$refs.tooltip
                if (!el) return
                const h = el.offsetHeight
                let top = rect.top
                if (top + h > vpH - TOOLTIP_MARGIN) {
                    top = Math.max(vpH - h - TOOLTIP_MARGIN, TOOLTIP_MARGIN)
                }
                this.tooltip.style = { ...this.tooltip.style, top: top + 'px', opacity: '1' }
            })
        },

        hideTooltip() {
            this.tooltip.visible = false
        },
    },
}
</script>

<style lang="scss">
@import '../../../../../../gulp-src/app/scss/_variables';
@import '../../../../../../gulp-src/app/scss/_mixins';

.nc-qro {
    flex: 1 1 auto;
    display: flex;
    flex-direction: row;
    overflow: hidden;
    background: $global-white-f6;

    // ── Левая колонка: категории ───────────────────
    &__sidebar {
        flex: 0 0 auto;
        width: 110px;
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        background: $global-white;
        border-right: 1px solid $global-light-gray;
        padding: 6px 0;
        @include scrollbar($thumb-color: $global-dark-3a, $track-color: $global-light-gray);
    }

    &__cat {
        flex: 0 0 auto;
        width: 100%;
        padding: 8px 10px;
        text-align: left;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.3;
        cursor: pointer;
        border: none;
        background: none;
        color: $global-gray;
        transition: background 0.15s ease, color 0.15s ease;
        word-break: break-word;

        &:hover {
            background: $global-white-f6;
            color: $global-black;
        }

        &.is-active {
            background: $global-white-f6;
            color: $global-dark-3a;
            font-weight: 600;
            box-shadow: inset 3px 0 0 $global-dark-3a;
        }
    }

    // ── Правая колонка: поиск + фразы ─────────────
    &__content {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        min-width: 0;
    }

    // ── Пошук ─────────────────────────────────────
    &__search {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        background: $global-white;
        border-bottom: 1px solid $global-light-gray;
    }

    &__search-icon {
        flex: 0 0 auto;
        color: $global-gray;
    }

    &__search-input {
        flex: 1 1 auto;
        border: none;
        outline: none;
        font-size: 13px;
        background: transparent;
        color: $global-black;
        min-width: 0;

        &::placeholder {
            color: $global-gray;
        }
    }

    &__search-clear {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        padding: 2px;
        cursor: pointer;
        color: $global-gray;

        &:hover { color: $global-black; }
    }

    // ── Список карток ──────────────────────────────
    &__list {
        flex: 1 1 auto;
        overflow-y: auto;
        padding: 8px;
        display: flex;
        flex-direction: column;
        gap: 6px;
        @include scrollbar($thumb-color: $global-dark-3a, $track-color: $global-light-gray);
    }

    // Картка фрази
    &__card {
        display: block;
        width: 100%;
        text-align: left;
        background: $global-white;
        border: 1.5px solid $global-light-gray;
        border-radius: 8px;
        padding: 10px 12px;
        cursor: pointer;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;

        &:hover {
            border-color: $global-purple;
            box-shadow: 0 2px 8px rgba(108, 79, 200, 0.1);
        }
    }

    &__card-text {
        margin: 0;
        font-size: 13px;
        line-height: 1.5;
        color: $global-black;
        white-space: pre-wrap;
        word-break: break-word;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__empty {
        padding: 20px 12px;
        font-size: 13px;
        color: $global-gray;
        text-align: center;
    }

    // ── Тултип с полным текстом ───────────────────
    &__tooltip {
        position: fixed;
        z-index: 9999;
        background: $global-white;
        border: 1.5px solid $global-light-gray;
        border-radius: 8px;
        padding: 10px 12px;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        font-size: 13px;
        line-height: 1.5;
        color: $global-black;
        white-space: pre-wrap;
        word-break: break-word;
        pointer-events: none;
        overflow-y: auto;
        @include scrollbar($thumb-color: $global-dark-3a, $track-color: $global-light-gray);
    }
}
</style>
